import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const TCG_ENUM = ["pokemon", "one_piece", "mtg", "yugioh", "lorcana", "digimon", "other"] as const;
const LANG_ENUM = ["english", "japanese", "chinese", "korean", "other"] as const;

const Input = z.object({
  tcg: z.enum(TCG_ENUM),
  sport: z.string().trim().min(1).max(60).nullable().optional(),
  title: z.string().trim().min(1).max(200),
  cardName: z.string().trim().min(1).max(200),
  conditionType: z.enum(["raw", "graded"]),
  conditionMin: z.string().trim().min(1).max(40),
  cardLanguage: z.enum(LANG_ENUM).nullable().optional(),
  maxPriceCents: z.number().int().positive().max(100_000_00),
  currency: z.string().length(3),
  quantity: z.number().int().min(1).max(99).default(1),
  description: z.string().max(4000).optional(),
  customRequests: z.string().max(2000).optional(),
  referenceImagePath: z.string().trim().min(1).max(500).optional(),
  priceIncludesShipping: z.boolean(),
  allowOffers: z.boolean().optional().default(false),
});

export const createBuyOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => Input.parse(data))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: row, error } = await supabase
      .from("buy_orders")
      .insert({
        buyer_id: userId,
        tcg: data.tcg,
        sport: data.sport ?? null,
        title: data.title,
        card_name: data.cardName,
        condition_type: data.conditionType,
        condition_min: data.conditionMin,
        card_language: data.cardLanguage ?? null,
        max_price_cents: data.maxPriceCents,
        currency: data.currency,
        quantity: data.quantity,
        description: data.description ?? null,
        custom_requests: data.customRequests ?? null,
        reference_image_path: data.referenceImagePath ?? null,
        price_includes_shipping: data.priceIncludesShipping,
        allow_offers: data.allowOffers ?? false,
      } as never)
      .select("id")
      .single();

    if (error) {
      console.error("[create-buy-order] insert failed", error);
      throw new Error(error.message);
    }
    return { id: row.id as string };
  });
